import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  Handshake,
  Users,
  UserCircle,
  Link as LinkIcon,
  Copy,
  Check,
  ExternalLink,
  Loader2,
  ChevronDown,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { LeaderGenealogy } from './LeaderGenealogy';
import { LinkMetaSheet } from './LinkMetaSheet';
import { useQrCode } from '../hooks/useQrCode';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DobradaDetailDrawer — shows full performance + genealogy + leaders for a
 * single dobrada. Lazy-fetches on open via the dobrada's own endpoints.
 */
export const DobradaDetailDrawer = ({ dobrada, open, onOpenChange, onRefresh }) => {
  const { activeTenant } = useAuth();
  const tenantSlug = activeTenant?.slug;
  const dobradaId = dobrada?.id;

  const { canEdit } = usePermissions();
  const canEditLinks = canEdit('links');

  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('overview');
  const [copied, setCopied] = useState(false);

  // Apoiadores tab state
  const [supporters, setSupporters] = useState([]);
  const [supportersLoading, setSupportersLoading] = useState(false);
  const [supportersPage, setSupportersPage] = useState(1);
  const [supportersTotal, setSupportersTotal] = useState(0);
  const supportersPerPage = 25;

  // Expandable leaders → each leader's links + per-link customization
  const [expandedLeaderId, setExpandedLeaderId] = useState(null);
  const [leaderLinks, setLeaderLinks] = useState({}); // { [leaderId]: link[] }
  const [leaderLinksLoading, setLeaderLinksLoading] = useState(null); // leaderId being loaded
  const [metaLink, setMetaLink] = useState(null);
  const [metaSheetOpen, setMetaSheetOpen] = useState(false);

  const publicUrl =
    dobrada && tenantSlug
      ? `${window.location.origin}/${tenantSlug}/d/${dobrada.slug}/cadastrar`
      : '';
  const qr = useQrCode(publicUrl);

  useEffect(() => {
    if (!open || !dobradaId) return;
    fetchLeaders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dobradaId]);

  const fetchLeaders = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/dobradas/${dobradaId}/leaders`,
        { withCredentials: true }
      );
      setLeaders(data || []);
    } catch (err) {
      toast.error('Erro ao carregar lideranças da dobrada');
    } finally {
      setLoading(false);
    }
  };

  const fetchSupporters = async (pageNum = 1) => {
    if (!dobradaId) return;
    setSupportersLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/dobradas/${dobradaId}/supporters`,
        { withCredentials: true, params: { page: pageNum, per_page: supportersPerPage } }
      );
      setSupporters(data.items || []);
      setSupportersTotal(data.total || 0);
      setSupportersPage(pageNum);
    } catch (err) {
      toast.error('Erro ao carregar apoiadores da dobrada');
    } finally {
      setSupportersLoading(false);
    }
  };

  // Lazy-fetch supporters when the tab opens
  useEffect(() => {
    if (tab === 'supporters' && open && dobradaId && supporters.length === 0) {
      fetchSupporters(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, open, dobradaId]);

  // Reset state when drawer closes or switches dobrada
  useEffect(() => {
    if (!open) {
      setSupporters([]);
      setSupportersTotal(0);
      setSupportersPage(1);
      setExpandedLeaderId(null);
      setLeaderLinks({});
      setTab('overview');
    }
  }, [open, dobradaId]);

  const toggleLeader = async (leaderId) => {
    if (expandedLeaderId === leaderId) {
      setExpandedLeaderId(null);
      return;
    }
    setExpandedLeaderId(leaderId);
    if (!leaderLinks[leaderId]) {
      setLeaderLinksLoading(leaderId);
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/leaders/${leaderId}/links`,
          { withCredentials: true }
        );
        setLeaderLinks((prev) => ({ ...prev, [leaderId]: data || [] }));
      } catch (err) {
        toast.error('Erro ao carregar links da liderança');
      } finally {
        setLeaderLinksLoading(null);
      }
    }
  };

  const openMetaSheet = (link) => {
    setMetaLink(link);
    setMetaSheetOpen(true);
  };

  const handleMetaSaved = () => {
    // Refetch the leader's links so the badges (with personalization)
    // reflect the saved state without a full page refresh.
    if (metaLink?.lideranca_id) {
      const leaderId = metaLink.lideranca_id;
      axios
        .get(`${BACKEND_URL}/api/leaders/${leaderId}/links`, { withCredentials: true })
        .then(({ data }) => {
          setLeaderLinks((prev) => ({ ...prev, [leaderId]: data || [] }));
        })
        .catch(() => {});
    }
    onRefresh?.();
  };

  const copyUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  if (!dobrada) return null;

  const color = dobrada.primary_color || '#7c3aed';
  const stats = dobrada.stats || {};

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-3xl overflow-y-auto p-0"
        data-testid="dobrada-detail-drawer"
      >
        {/* Header */}
        <div
          className="px-6 py-5 text-white"
          style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
        >
          <SheetHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center">
                <Handshake className="h-5 w-5" />
              </div>
              <div>
                <SheetTitle className="text-white text-xl">{dobrada.nome}</SheetTitle>
                <SheetDescription className="text-white/80 text-xs">
                  {dobrada.cargo}
                  {dobrada.estado ? ` · ${dobrada.estado}` : ''}
                  {dobrada.numero ? ` · nº ${dobrada.numero}` : ''}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* KPIs */}
          <div className="grid grid-cols-3 gap-3">
            <MiniStat
              label="Lideranças"
              value={stats.liderancas_count || 0}
              icon={<Users className="h-4 w-4" />}
              color={color}
            />
            <MiniStat
              label="Apoiadores"
              value={stats.apoiadores_count || 0}
              icon={<UserCircle className="h-4 w-4" />}
              color="#3b82f6"
            />
            <MiniStat
              label="Links ativos"
              value={stats.links_count || 0}
              icon={<LinkIcon className="h-4 w-4" />}
              color="#10b981"
            />
          </div>

          {/* Public URL of dobrada signup */}
          {publicUrl && (
            <Card>
              <CardContent className="pt-4 pb-4 space-y-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
                    Link público de cadastro de lideranças
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Compartilhe para que apoiadores virem lideranças vinculadas a esta dobrada.
                  </p>
                </div>
                <div className="flex gap-2 items-start">
                  {qr && (
                    <div className="rounded-lg border border-slate-200 p-1.5 bg-white shrink-0">
                      <img
                        src={qr}
                        alt="QR de cadastro de lideranças"
                        className="w-20 h-20"
                        data-testid="dobrada-qr"
                      />
                    </div>
                  )}
                  <div className="flex-1 space-y-2 min-w-0">
                    <div className="flex gap-2">
                      <Input
                        value={publicUrl}
                        readOnly
                        className="font-mono text-[10px] h-9"
                        data-testid="dobrada-public-url"
                      />
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={copyUrl}
                        className="h-9 w-9 shrink-0"
                        data-testid="dobrada-copy-url-btn"
                      >
                        {copied ? (
                          <Check className="h-3.5 w-3.5 text-emerald-600" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </Button>
                      <Button size="icon" variant="outline" asChild className="h-9 w-9 shrink-0">
                        <a href={publicUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabs */}
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="overview" data-testid="dobrada-tab-overview">
                Visão geral
              </TabsTrigger>
              <TabsTrigger value="leaders" data-testid="dobrada-tab-leaders">
                Lideranças
              </TabsTrigger>
              <TabsTrigger value="supporters" data-testid="dobrada-tab-supporters">
                Apoiadores
              </TabsTrigger>
              <TabsTrigger value="tree" data-testid="dobrada-tab-tree">
                Árvore
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-3 mt-4">
              <DetailRow label="Cargo" value={dobrada.cargo} />
              <DetailRow label="Número de urna" value={dobrada.numero || '—'} />
              <DetailRow label="Partido" value={dobrada.partido || '—'} />
              <DetailRow
                label="Estado / cidades"
                value={
                  dobrada.estado
                    ? `${dobrada.estado}${
                        dobrada.cidades?.length ? ` · ${dobrada.cidades.join(', ')}` : ''
                      }`
                    : '—'
                }
              />
              <DetailRow
                label="Subtítulo da captação"
                value={dobrada.capture_subtitle || '—'}
              />
              <DetailRow label="Status" value={dobrada.status} />
              {dobrada.observacoes && (
                <DetailRow label="Observações" value={dobrada.observacoes} />
              )}
            </TabsContent>

            <TabsContent value="leaders" className="space-y-3 mt-4">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : leaders.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">
                  Nenhuma liderança vinculada ainda. Compartilhe o link público acima para começar.
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500">
                    Clique na liderança para ver os links vinculados a ela e personalizar cada um.
                  </p>
                  {leaders.map((ld) => {
                    const expanded = expandedLeaderId === ld.id;
                    const links = leaderLinks[ld.id] || [];
                    const loadingLinks = leaderLinksLoading === ld.id;
                    return (
                      <Card key={ld.id} className="overflow-hidden" data-testid={`dobrada-leader-card-${ld.id}`}>
                        <button
                          type="button"
                          onClick={() => toggleLeader(ld.id)}
                          className="w-full px-3 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors text-left"
                          data-testid={`expand-leader-${ld.id}`}
                        >
                          {expanded ? (
                            <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm text-slate-900 truncate">
                              {ld.apelido || ld.nome_completo}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              {ld.cidade || '—'}
                              {ld.tipo ? ` · ${ld.tipo}` : ''}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-base font-bold text-slate-900">
                              {ld.stats?.total_redirecionamentos || 0}
                            </div>
                            <div className="text-[10px] uppercase text-slate-500">apoios</div>
                          </div>
                        </button>
                        {expanded && (
                          <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-3">
                            {loadingLinks ? (
                              <div className="flex justify-center py-4">
                                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                              </div>
                            ) : links.length === 0 ? (
                              <p className="text-xs text-slate-500 text-center py-3">
                                Nenhum link vinculado a esta liderança.
                              </p>
                            ) : (
                              <div className="space-y-1.5">
                                {links.map((lk) => {
                                  const customized =
                                    !!(lk.og_title || lk.og_description || lk.og_image_url ||
                                       lk.capture_hero_image_url || lk.capture_subtitle);
                                  return (
                                    <div
                                      key={lk.id}
                                      className="flex items-center gap-2 bg-white rounded-md border border-slate-200 px-2.5 py-2"
                                      data-testid={`leader-link-${lk.id}`}
                                    >
                                      <div className="flex-1 min-w-0">
                                        <div className="font-mono text-xs text-slate-900 truncate">
                                          /{lk.token}
                                        </div>
                                        <div className="text-[10px] text-slate-500 truncate">
                                          → {lk.destination_name || 'sem destino'}
                                          {lk.cidade ? ` · ${lk.cidade}` : ''}
                                          {customized && (
                                            <span className="ml-1.5 text-emerald-600 font-semibold">
                                              · personalizado
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant={customized ? 'default' : 'outline'}
                                        className={
                                          customized
                                            ? 'h-8 bg-emerald-600 hover:bg-emerald-700 gap-1'
                                            : 'h-8 gap-1'
                                        }
                                        onClick={() => openMetaSheet(lk)}
                                        disabled={!canEditLinks}
                                        data-testid={`personalize-link-${lk.id}`}
                                      >
                                        <Sparkles className="h-3 w-3" />
                                        Personalizar
                                      </Button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="supporters" className="space-y-3 mt-4">
              {supportersLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : supportersTotal === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">
                  Nenhum apoiador captado pelas lideranças desta dobrada ainda.
                </p>
              ) : (
                <>
                  <p className="text-xs text-slate-500">
                    {supportersTotal} apoiadores identificados pelas lideranças desta dobrada.
                  </p>
                  <Card>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                          <tr>
                            <th className="text-left px-3 py-2 font-bold">Nome</th>
                            <th className="text-left px-3 py-2 font-bold">Liderança</th>
                            <th className="text-left px-3 py-2 font-bold">Cidade</th>
                            <th className="text-left px-3 py-2 font-bold">WhatsApp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {supporters.map((s) => (
                            <tr
                              key={s.id}
                              className="border-t border-slate-100"
                              data-testid={`dobrada-supporter-row-${s.id}`}
                            >
                              <td className="px-3 py-2 font-medium text-slate-900">
                                {s.nome || '—'}
                              </td>
                              <td className="px-3 py-2 text-slate-700">
                                {s.lideranca_nome || '—'}
                              </td>
                              <td className="px-3 py-2 text-slate-700">{s.cidade || '—'}</td>
                              <td className="px-3 py-2 font-mono text-xs text-slate-600">
                                {s.whatsapp || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                  {supportersTotal > supportersPerPage && (
                    <div className="flex items-center justify-between gap-3 pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={supportersPage === 1 || supportersLoading}
                        onClick={() => fetchSupporters(supportersPage - 1)}
                      >
                        Anterior
                      </Button>
                      <span className="text-xs text-slate-500">
                        Página {supportersPage} de{' '}
                        {Math.ceil(supportersTotal / supportersPerPage)}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={
                          supportersPage >= Math.ceil(supportersTotal / supportersPerPage) ||
                          supportersLoading
                        }
                        onClick={() => fetchSupporters(supportersPage + 1)}
                      >
                        Próxima
                      </Button>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="tree" className="mt-4">
              <LeaderGenealogy
                endpoint={`${BACKEND_URL}/api/dobradas/${dobradaId}/genealogy`}
              />
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>

      {/* Per-link personalization sheet — reused from LinksPage */}
      <LinkMetaSheet
        link={metaLink}
        leader={metaLink ? leaders.find((l) => l.id === metaLink.lideranca_id) || null : null}
        open={metaSheetOpen}
        onOpenChange={(v) => {
          setMetaSheetOpen(v);
          if (!v) setMetaLink(null);
        }}
        onSaved={handleMetaSaved}
        canEdit={canEditLinks}
      />
    </Sheet>
  );
};

const MiniStat = ({ label, value, icon, color }) => (
  <Card>
    <CardContent className="pt-4 pb-4">
      <div className="flex items-center gap-2 text-slate-500" style={{ color }}>
        {icon}
        <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span>
      </div>
      <div className="text-2xl font-black text-slate-900 mt-1">{value}</div>
    </CardContent>
  </Card>
);

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
    <span className="text-xs uppercase tracking-wider text-slate-500 font-bold sm:w-44 shrink-0">
      {label}
    </span>
    <span className="text-sm text-slate-900 break-words">{value}</span>
  </div>
);
